import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'

import { useDispatch, useSelector } from 'react-redux'
import {
  getGirderState,
  getJackState,
  getLeftHipState,
  getTrussState,
} from '../../../../../store/selectors'
import { girderActions } from '../../../../../store/slices'

import {
  Accordion,
  Dropdown,
  FormControl,
  FormGroup,
  FormLabel,
} from '../../../../../../../components'

import { CONNECTION_TYPE, GIRDER_TYPE_LABEL, MEMBER_TYPE } from '../../../../../common/constants'
import {
  getDefaultOptValue,
  getDefaultPlyValue,
  getGAValue,
  isMemberTypeSolidSawn,
  isMultiTrussConnectionType,
  parseFeetInchesSixteenths,
  shouldDisplayNumberOfPlies,
} from '../../../../../common/fns'
import { useCalcActions, useCommonOptions } from '../../../../../common/hooks'
import { girder } from '../../../../../common/json/carrying-member.json'
import { helpText } from '../../../../../common/json/helpText.json'

import { MEASURE_UNITS } from '../../../../../../../utils/constants'
import { RenderIf, isOneDropdownOption } from '../../../../../../../utils/fns'

const Girder = (props) => {
  const { country, connectionType } = props

  const dispatch = useDispatch()
  const state = useSelector(getGirderState)

  const trussState = useSelector(getTrussState)
  const jackState = useSelector(getJackState)
  const leftHipState = useSelector(getLeftHipState)

  const { errors, validateField, setCalcValue, updateCalcValue } = useCalcActions(girderActions)

  const { materialOptions, widthOptions, depthOptions, pliesOptions } = useCommonOptions(
    state,
    country,
    girder,
  )

  const filterdMemberTypeOptions = useMemo(() => {
    if (!isMultiTrussConnectionType(connectionType)) return girder.memberTypeData

    return girder.memberTypeData.filter(
      (item) => item.value !== MEMBER_TYPE.MASONRY && item.value !== MEMBER_TYPE.CONCRETE,
    )
  }, [connectionType])

  const handleChangeMemberType = (e) => updateCalcValue({ ...girder.defaultValue[e.target.value] })

  const handleChangeFIS = (e) => {
    const value = parseFeetInchesSixteenths(e.target.value)

    validateField(e)
    updateCalcValue({ [e.target.name]: value })
  }

  const isMemberTypeDisable = useMemo(() => {
    return (
      (connectionType === CONNECTION_TYPE.TRUSS && trussState.type !== 'truss') ||
      (connectionType === CONNECTION_TYPE.MULTI_TRUSS && jackState.type !== 'truss') ||
      leftHipState.type !== 'truss'
    )
  }, [connectionType, trussState.type, jackState.type, leftHipState.type])

  useEffect(() => {
    const isWidthExists = widthOptions.some((item) => item.value === state.width)

    if (!isWidthExists && widthOptions.length > 0) {
      const value = getDefaultOptValue(widthOptions)
      dispatch(girderActions.modifyCalculation({ name: 'width', value }))
    }
  }, [state.material])

  useEffect(() => {
    const isDepthExists = depthOptions.some((item) => item.value === state.depth)

    if (!isDepthExists && depthOptions.length > 0) {
      const value = getDefaultOptValue(depthOptions)
      dispatch(girderActions.modifyCalculation({ name: 'depth', value }))
    }
  }, [state.width])

  useEffect(() => {
    const isPlyExists = pliesOptions.some((item) => item.value === state.ply)

    if (!isPlyExists) {
      dispatch(
        girderActions.modifyCalculation({ name: 'ply', value: getDefaultPlyValue(pliesOptions) }),
      )
    }
  }, [state.depth, pliesOptions])

  useEffect(() => {
    const isMemberTypeExists = filterdMemberTypeOptions.some((item) => item.value === state.type)

    if (!isMemberTypeExists) {
      updateCalcValue({ ...girder.defaultValue[getDefaultOptValue(filterdMemberTypeOptions)] })
    }
  }, [filterdMemberTypeOptions, state.type])

  return (
    <Accordion title="Girder (Carrying Member)">
      <div className="row">
        <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
          <FormGroup>
            <FormLabel htmlFor="girder.type">Type</FormLabel>
            <Dropdown
              name="girder.type"
              value={state.type}
              options={filterdMemberTypeOptions}
              onChange={handleChangeMemberType}
              disabled={isMemberTypeDisable}
              data-ga4={`girder_memberType:${GIRDER_TYPE_LABEL[state.type]}`}
              data-testid="girder.type"
            />
            {RenderIf(
              isMemberTypeDisable,
              <span className="input-error is-visible mt-1">
                For non-Truss connections, use the Joist Connection Type
              </span>,
            )}
          </FormGroup>
        </div>

        <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
          <FormGroup>
            <FormLabel htmlFor="girder.material">Lumber Species</FormLabel>
            <Dropdown
              name="girder.material"
              value={state.material}
              options={materialOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(materialOptions)}
              data-ga4={`girder_lumberSpecies:${getGAValue(materialOptions, state.material)}`}
              data-testid="girder.material"
            />
          </FormGroup>
        </div>

        <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
          <FormGroup>
            <FormLabel htmlFor="girder.width">Bottom Chord Width</FormLabel>
            <Dropdown
              name="girder.width"
              value={state.width}
              options={widthOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(widthOptions)}
              data-ga4={`girder_width:${getGAValue(widthOptions, state.width)}`}
              data-testid="girder.width"
            />
          </FormGroup>
        </div>

        <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
          <FormGroup>
            <FormLabel htmlFor="girder.depth">Bottom Chord Height</FormLabel>
            <Dropdown
              name="girder.depth"
              value={state.depth}
              options={depthOptions}
              onChange={setCalcValue}
              disabled={isOneDropdownOption(depthOptions)}
              data-ga4={`girder_depth:${getGAValue(depthOptions, state.depth)}`}
              data-testid="girder.depth"
            />
          </FormGroup>
        </div>

        {shouldDisplayNumberOfPlies(state.type, pliesOptions) && (
          <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
            <FormGroup>
              <FormLabel htmlFor="girder.ply">Number of Plies</FormLabel>
              <Dropdown
                name="girder.ply"
                value={state.ply}
                options={pliesOptions}
                onChange={setCalcValue}
                disabled={isOneDropdownOption(pliesOptions)}
                data-ga4={`girder_ply:${getGAValue(pliesOptions, state.ply)}`}
                data-testid="girder.ply"
              />
            </FormGroup>
          </div>
        )}

        {isMemberTypeSolidSawn(state.type) && (
          <>
            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
              <FormGroup>
                <FormLabel htmlFor="girder.kingWidth">Vertical Width (King Post)</FormLabel>
                <FormControl
                  name="girder.kingWidth"
                  value={state.kingWidth}
                  onChange={handleChangeFIS}
                  addonAfter={MEASURE_UNITS.INCH}
                  error={errors['girder.kingWidth']}
                  data-ga4={`girder_kingWidth:${state.kingWidth}`}
                  data-testid="girder.kingWidth"
                />
              </FormGroup>
            </div>

            <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
              <FormGroup>
                <FormLabel htmlFor="girder.kingHeight" popperText={helpText[country].totalHeight}>
                  Total Height
                </FormLabel>
                <FormControl
                  name="girder.kingHeight"
                  value={state.kingHeight}
                  onChange={handleChangeFIS}
                  addonAfter={MEASURE_UNITS.INCH}
                  error={errors['girder.kingHeight']}
                  data-ga4={`girder_kingHeight:${state.kingHeight}`}
                  data-testid="girder.kingHeight"
                />
              </FormGroup>
            </div>
          </>
        )}

        <div className="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-3 col-xxl-3">
          <FormGroup>
            <FormLabel htmlFor="girder.memberID">Member ID</FormLabel>
            <FormControl
              name="girder.memberID"
              value={state.memberID}
              onChange={(e) => {
                validateField(e)
                setCalcValue(e)
              }}
              error={errors['girder.memberID']}
              data-ga4={`girder_memberID:${state.memberID}`}
              data-testid="girder.memberID"
            />
          </FormGroup>
        </div>
      </div>
    </Accordion>
  )
}

Girder.propTypes = {
  country: PropTypes.string.isRequired,
  connectionType: PropTypes.string.isRequired,
}

export default Girder
